import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useTenant } from '@/hooks/useTenant';

export function AdminTenantOnboardingStatusPage() {
  const { tenantId } = useParams({ from: '/admin/tenants/onboarding/$tenantId/status' });
  const queryClient = useQueryClient();
  const { isAdmin } = useTenant();
  const [template, setTemplate] = useState<'minimal' | 'sales'>('minimal');

  const statusQuery = useQuery({
    queryKey: ['tenant-onboarding-status', tenantId],
    queryFn: () => api.getTenantOnboardingStatus(tenantId),
    enabled: isAdmin,
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryTenantOnboardingSeeding(tenantId, { template }),
    onSuccess: () => {
      toast.success('Starter-data seeding retry completed');
      queryClient.invalidateQueries({ queryKey: ['tenant-onboarding-status', tenantId] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to retry starter-data seeding');
    },
  });

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Tenant Onboarding Status</h1>
          <p className="text-muted-foreground">Admin access is required to view this page.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Tenant Onboarding Status</h1>
            <p className="text-muted-foreground mt-1">
              Review seeding progress and retry if needed.
            </p>
          </div>
          <Link
            to="/admin/tenants/onboarding"
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            Start another onboarding
          </Link>
        </div>

        {statusQuery.isLoading ? (
          <div className="rounded-xl border border-border bg-card p-4 text-muted-foreground">
            Loading onboarding status...
          </div>
        ) : statusQuery.isError ? (
          <div className="rounded-xl border border-border bg-card p-4 text-red-600">
            {statusQuery.error instanceof Error
              ? statusQuery.error.message
              : 'Failed to load onboarding status'}
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-border bg-card p-4 space-y-2">
              <h2 className="text-lg font-semibold text-foreground">Current status</h2>
              <p className="text-sm">
                <span className="text-muted-foreground">Tenant ID:</span>{' '}
                {statusQuery.data?.tenantId}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Seeding:</span> {statusQuery.data?.seeding}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Assignments created:</span>{' '}
                {statusQuery.data?.assignmentsCreated ?? 0}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Updated at:</span>{' '}
                {statusQuery.data?.updatedAt
                  ? new Date(statusQuery.data.updatedAt).toLocaleString()
                  : 'n/a'}
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Warnings</h2>
              {statusQuery.data?.warnings.length ? (
                <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
                  {statusQuery.data.warnings.map((warning, index) => (
                    <li key={`${warning}-${index}`}>{warning}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No warnings recorded.</p>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Starter-data retry</h2>
              <div className="grid gap-3 sm:grid-cols-3">
                <Select
                  label="Template"
                  value={template}
                  onChange={event => setTemplate(event.target.value as 'minimal' | 'sales')}
                >
                  <option value="minimal">Minimal</option>
                  <option value="sales">Sales</option>
                </Select>
                <div className="sm:col-span-2 flex items-end">
                  <Button
                    onClick={() => retryMutation.mutate()}
                    disabled={retryMutation.isPending}
                    className="w-full sm:w-auto"
                  >
                    {retryMutation.isPending ? 'Retrying...' : 'Retry starter-data seeding'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
